import {
    roundDecimalOrNATruthyZero,
    NA_DEFAULT,
    displayShortDateOrNA
} from '../../utils/Format/Format';

export const mkVolatilityMeasuresUtil = ({ etf, bond, moneyMarket }) => (
    gqlResult
) => {
    if (!gqlResult) return null;
    const {
        mutualFund: {
            threeYearRiskMeasure: {
                beta,
                alpha,
                standardDeviation,
                activeShare,
                rSquared
            },
            monthlyComponentsAsOfDate: asOfDate,
            // commenting as per US84370 - not to show for any fund for now
            // earningsGrowthRate3Years,
            // earningsPerShareGrowthCurrentFiscalYear,
            // priceEarningsRatio,
            // priceEarningsRatioCurrentFiscalYear,
            // turnoverRate,
            turnoverRateTwelveMonth,
            morningStarRatings
        }
    } = gqlResult;

    // commenting as per US84370 - not to show for any fund for now
    // const validateStock =
    //     name === 'Domestic Stock' ||
    //     name === 'International/Global Stock' ||
    //     ticker === 'RPBAX';

    const { morningStarCategoryName } = morningStarRatings || {};

    const returnData = [
        !(bond || moneyMarket || etf) &&
            activeShare && {
                // hide for bond per US83110
                label: 'Active Share',
                values: [{ val1: roundDecimalOrNATruthyZero(activeShare) }],
                showDefinition: false,
                definition: `A measure, on a scale of 0 to 100, of how closely a fund's holdings correlate to the companies represented in its primary benchmark index shown in its prospectus. An Active Share of 0 means that the fund has the same holdings as the benchmark and an Active Share of 100 means that the fund has no common holdings with the benchmark. Active Share can be useful as a risk measure for funds that invest primarily in stocks. However, there are multiple types of other measures that can help an investor evaluate potential volatility and fund performance, and investors should consider Active Share in combination with other quantitative and qualitative information. Investors should be aware that a fund with high Active Share may expose investors to greater price volatility and performance that varies significantly in comparison to the fund's benchmark index.`
            },
        !moneyMarket && {
            label: 'Alpha (%)',
            values: [{ val1: roundDecimalOrNATruthyZero(alpha) }],
            showDefinition: false,
            definition: `Three-year risk measure shown. Coefficient measuring the portion of an investment's return arising from nonmarket risk. Alpha represents the difference between a fund's actual returns and its expected performance (i.e., the relationship between the fund's performance and its beta over a three-year period). An investment whose price is low relative to its alpha is undervalued and considered a good selection.`
        },
        morningStarCategoryName !== 'Nontraditional Bond' && {
            label: 'Beta',
            values: [{ val1: roundDecimalOrNATruthyZero(beta) }],
            showDefinition: false,
            definition: `Three-year risk measure shown. Beta is a statistical measure of how volatile a fund's returns have been compared with an appropriate benchmark index such as the S&P 500 Index. The beta of every index is 1.00, no matter how volatile the index is. If a fund's beta is higher than 1.00, its returns have fluctuated more than its benchmark's. A beta lower than 1.00 means the fund has been less volatile than the benchmark.`
        },
        {
            label: 'R',
            labelSup: '2',
            values: [{ val1: roundDecimalOrNATruthyZero(rSquared) }],
            showDefinition: false,
            definition: `Three-year risk measure shown. R-squared measures how much a fund's past returns can be explained by the returns from its benchmark index. If a fund's total returns were precisely synchronized with the index's return, its R-squared would be 1.00 (100%). If a fund's return bore no relationship to the index's return, its R-squared would be 0. The higher the R-squared, the more the fund's return can be explained by the performance of the index, and so the performance of the market or market segment. The lower the R-squared, the more the return can be explained by the fund manager's decisions.`
        },
        {
            label: 'Standard Deviation (%)',
            values: [{ val1: roundDecimalOrNATruthyZero(standardDeviation) }],
            showDefinition: false,
            definition: `Three-year risk measure shown based on 36 month-end returns. Standard Deviation is a statistical measure of the historic volatility of a mutual fund or portfolio. More generally, it is a measure of the extent to which numbers are spread around their average. The wider the dispersions, the larger the standard deviation. The higher the deviation, the greater the volatility. This is an independent measure of volatility; it is not relative to an index.`
        },
        !(bond || moneyMarket) && {
            // hide for bond per US83110
            label: 'Annual Turnover Rate',
            values: [
                { val1: roundDecimalOrNATruthyZero(turnoverRateTwelveMonth) }
            ],
            showDefinition: false,
            definition: `A percentage of holdings a fund buys and sells during a year.`
        }

        // show for below conditions as per US82388
        // commenting as per US84370 - not to show for any fund for now

        // validateStock && {
        //     label: `Price/Earnings Ratio (${
        //         name === 'Domestic Stock' || ticker === 'RPBAX'
        //             ? 'Domestic'
        //             : 'International'
        //     })`,
        //     values: [
        //         {
        //             val1:
        //                 name === 'International/Global Stock'
        //                     ? roundDecimalOrNA(
        //                           priceEarningsRatioCurrentFiscalYear
        //                       )
        //                     : roundDecimalOrNA(priceEarningsRatio)
        //         }
        //     ],
        //     showDefinition: false,
        //     definition: `The price-to-earnings ratio shows the "multiple" of earnings at which a stock is selling. The P/E ratio is calculated by dividing a stock's current price by its current earnings per share. A high multiple means that investors are optimistic about future growth and have bid up the stock's price.`
        // },
        // validateStock && {
        //     label: `Earnings Growth Rate (${
        //         name === 'Domestic Stock' || ticker === 'RPBAX'
        //             ? 'Domestic'
        //             : 'International'
        //     })`,
        //     values: [
        //         {
        //             val1:
        //                 name === 'International/Global Stock'
        //                     ? roundDecimalOrNA(
        //                           earningsPerShareGrowthCurrentFiscalYear
        //                       )
        //                     : roundDecimalOrNA(earningsGrowthRate3Years)
        //         }
        //     ],
        //     showDefinition: false,
        //     definition: `The rate at which a company's earnings are expected to increase in the future. The Earnings Growth Rate for Domestic Stocks is based upon the projected earnings and the Earnings Growth Rate for Foreign Stocks is based upon the fund's current fiscal year.`
        // }
    ].filter((truthy) => truthy); // remove conditional indices
    return {
        data: returnData,
        asOfDate: displayShortDateOrNA(asOfDate)
    };
};

export const etfVolatilityMeasuresUtil = (isFI) => (gqlResult) => {
    if (!gqlResult) return null;
    const {
        exchangeTradedFund: {
            benchmarks,
            riskMeasure,
            portfolioCharacteristics
        }
    } = gqlResult;

    const { asOfDate, fundRiskMeasure, benchmarkRiskMeasure } =
        riskMeasure || {};
    const {
        standardDeviation: fundStandardDeviation,
        rSquared: fundRSquared,
        beta: fundBeta,
        alpha: fundAlpha
    } =
        fundRiskMeasure || {};

    const { fundPortfolioCharacteristic } = portfolioCharacteristics || {};
    const { weightedAverageMarketCap } =
        fundPortfolioCharacteristic || NA_DEFAULT;

    const primaryBenchmark =
        benchmarks && benchmarks.find((bm) => bm.benchmarkType === 'Primary');
    const primaryBenchmarkRiskMeasure =
        primaryBenchmark &&
        benchmarkRiskMeasure &&
        benchmarkRiskMeasure.length > 0 &&
        benchmarkRiskMeasure.find(
            (bm) => bm.benchmarkCode === primaryBenchmark.benchmarkCode
        );

    const etfReturnData = [
        {
            label: 'Beta',
            values: [
                {
                    val1: roundDecimalOrNATruthyZero(fundBeta)
                },
                {
                    val2: roundDecimalOrNATruthyZero(
                        primaryBenchmarkRiskMeasure &&
                            primaryBenchmarkRiskMeasure.beta
                    )
                }
            ],
            showDefinition: false,
            definition: `Three-year risk measure shown. Beta is a statistical measure of how volatile a fund's returns have been compared with an appropriate benchmark index such as the S&P 500 Index. The beta of every index is 1.00, no matter how volatile the index is. If a fund's beta is higher than 1.00, its returns have fluctuated more than its benchmark's. A beta lower than 1.00 means the fund has been less volatile than the benchmark.`
        },
        {
            label: 'R',
            labelSup: '2',
            values: [
                {
                    val1: roundDecimalOrNATruthyZero(fundRSquared)
                },
                {
                    val2: roundDecimalOrNATruthyZero(
                        primaryBenchmarkRiskMeasure &&
                            primaryBenchmarkRiskMeasure.rSquared
                    )
                }
            ],
            showDefinition: false,
            definition: `Three-year risk measure shown. R-squared measures how much a fund's past returns can be explained by the returns from its benchmark index. If a fund's total returns were precisely synchronized with the index's return, its R-squared would be 1.00 (100%). If a fund's return bore no relationship to the index's return, its R-squared would be 0. The higher the R-squared, the more the fund's return can be explained by the performance of the index, and so the performance of the market or market segment. The lower the R-squared, the more the return can be explained by the fund manager's decisions.`
        },
        {
            label: 'Standard Deviation (%)',
            values: [
                {
                    val1: roundDecimalOrNATruthyZero(fundStandardDeviation)
                },
                {
                    val2: 'N/A'
                }
            ],
            showDefinition: false,
            definition: `Three-year risk measure shown based on 36 month-end returns. Standard Deviation is a statistical measure of the historic volatility of a mutual fund or portfolio. More generally, it is a measure of the extent to which numbers are spread around their average. The wider the dispersions, the larger the standard deviation. The higher the deviation, the greater the volatility. This is an independent measure of volatility; it is not relative to an index.`
        },
        {
            label: 'Weighted Avg Market Cap ($)',
            values: [
                {
                    val1: roundDecimalOrNATruthyZero(weightedAverageMarketCap)
                },
                {
                    val2: 'N/A'
                }
            ],
            showDefinition: false,
            definition: `Weighted average market capitalization is a type of market index in which each component is weighted according to the size of its total market capitalization. Market capitalization is the sum of the total value of a company's outstanding shares multiplied by the price of one share.`
        }
    ];

    const returnData = isFI
        ? [
              {
                  label: 'Alpha (%)',
                  values: [
                      {
                          val1: roundDecimalOrNATruthyZero(fundAlpha)
                      },
                      {
                          val2: roundDecimalOrNATruthyZero(
                              primaryBenchmarkRiskMeasure &&
                                  primaryBenchmarkRiskMeasure.alpha
                          )
                      }
                  ],
                  showDefinition: false,
                  definition: `Three-year risk measure shown. Coefficient measuring the portion of an investment's return arising from nonmarket risk. Alpha represents the difference between a fund's actual returns and its expected performance (i.e., the relationship between the fund's performance and its beta over a three-year period). An investment whose price is low relative to its alpha is undervalued and considered a good selection.`
              },
              ...etfReturnData.filter(
                  (dt) => dt.label !== 'Weighted Avg Market Cap ($)'
              )
          ]
        : etfReturnData;

    return {
        data: returnData,
        asOfDate: displayShortDateOrNA(asOfDate),
        headers: ['', 'ETF', 'Benchmark*'],
        primaryBenchmark
    };
};
